import { AI_LAB_COURSES, TIME_SLOTS } from "../../constants/ai-lab-constants";

// Helper to calculate the next date for a given day name (e.g. "Friday")
export const getNextDateForDay = (dayName) => {
  if (!dayName) return null;
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const targetDay = daysOfWeek.findIndex(d => d.toLowerCase() === dayName.trim().toLowerCase());
  if (targetDay === -1) return null;

  const now = new Date();
  const currentDay = now.getDay();

  let daysToAdd = targetDay - currentDay;
  if (daysToAdd < 0) {
    daysToAdd += 7; // Target day is next week
  }

  const resultDate = new Date();
  resultDate.setDate(now.getDate() + daysToAdd);
  return resultDate;
};

// Helper to format date as "26 Jun 2026"
export const formatDate = (date) => {
  if (!date) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper to calculate upcoming class date and start time
export const getNextClassDateTime = (day, timeSlot) => {
  if (!day || !timeSlot) return null;
  const nextDate = getNextDateForDay(day);
  if (!nextDate) return null;

  const parts = timeSlot.split(/\s*[-–—]\s*/);
  const startTimePart = parts[0]?.trim();
  if (!startTimePart) return nextDate;

  const match = startTimePart.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!match) return nextDate;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && hours < 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;

  nextDate.setHours(hours, minutes, 0, 0);

  const now = new Date();
  if (nextDate.toDateString() === now.toDateString()) {
    const classEnd = new Date(nextDate.getTime() + 60 * 60 * 1000);
    if (now.getTime() > classEnd.getTime()) {
      nextDate.setDate(nextDate.getDate() + 7);
    }
  }

  return nextDate;
};

// Helper to check if current time is within 15 minutes before or during the class
export const isClassActive = (day, timeSlot) => {
  try {
    if (!day || !timeSlot || day === "To be confirmed") return false;

    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const now = new Date();
    const currentDayName = daysOfWeek[now.getDay()];

    if (currentDayName.toLowerCase() !== day.trim().toLowerCase()) {
      return false;
    }

    const parts = timeSlot.split(/\s*[-–—]\s*/);
    const timePart = parts[0]?.trim();
    const match = timePart.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return false;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    const classStart = new Date();
    classStart.setHours(hours, minutes, 0, 0);

    let classEnd = new Date(classStart.getTime() + 60 * 60 * 1000);
    const endPart = parts[1]?.trim();
    const endMatch = endPart?.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (endMatch) {
      let endHours = parseInt(endMatch[1], 10);
      const endMinutes = parseInt(endMatch[2], 10);
      const endAmpm = endMatch[3].toUpperCase();
      if (endAmpm === "PM" && endHours < 12) endHours += 12;
      if (endAmpm === "AM" && endHours === 12) endHours = 0;
      classEnd = new Date();
      classEnd.setHours(endHours, endMinutes, 0, 0);
    }

    const fifteenMinsMs = 15 * 60 * 1000;
    const nowTime = now.getTime();

    return nowTime >= (classStart.getTime() - fifteenMinsMs) && nowTime <= classEnd.getTime();
  } catch (e) {
    console.error("Error parsing class active state:", e);
    return false;
  }
};

// Helper to get Google Meet / batch link
export const getJoinLink = (childName, courseId, day, timeSlot, batches = []) => {
  const matchingBatch = batches.find(b => {
    const batchTimeLabel = TIME_SLOTS.find(t => t.id === b.timeSlot)?.label || b.timeSlot;
    return b.courseId === courseId &&
      b.day === day &&
      batchTimeLabel === timeSlot;
  });

  if (matchingBatch && matchingBatch.meetLink) {
    return matchingBatch.meetLink;
  }

  // Fallback to WhatsApp
  const scheduleStr = `${day} at ${timeSlot}`;
  const message = `Hello, I'd like to join the live class for ${childName} enroled in AI Lab (${scheduleStr}).`;
  return `https://wa.me/919560734660?text=${encodeURIComponent(message)}`;
};

// Helper to calculate exact age in years from DOB string (YYYY-MM-DD)
export const calculateAgeFromDob = (dobString) => {
  if (!dobString) return null;
  const birthDate = new Date(dobString);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age >= 0 && age < 120 ? age : null;
};

// Helper to format class schedule for display e.g. "Today, 4:00 PM" or "Friday, 5:00 PM"
export const formatClassScheduleString = (day, timeSlot, nextDate) => {
  if (!day || !timeSlot) return "";
  const now = new Date();
  const startTimePart = timeSlot.split(/\s*[-–—]\s*/)[0]?.trim() || timeSlot;

  if (nextDate) {
    const isToday = nextDate.toDateString() === now.toDateString();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = nextDate.toDateString() === tomorrow.toDateString();

    if (isToday) return `Today, ${startTimePart}`;
    if (isTomorrow) return `Tomorrow, ${startTimePart}`;
    return `${day}, ${startTimePart}`;
  }

  return `${day}, ${startTimePart}`;
};

// Helper to format class time with (IST) matching Image 4
export const formatUpcomingClassTime = (day, timeSlot, nextDate) => {
  if (!timeSlot) return "";
  const parts = timeSlot.split(/\s*[-–—]\s*/);
  const startTimePart = parts[0]?.trim() || timeSlot;

  if (nextDate) {
    const now = new Date();
    const isToday = nextDate.toDateString() === now.toDateString();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = nextDate.toDateString() === tomorrow.toDateString();

    if (isToday) return `Today, ${startTimePart} (IST)`;
    if (isTomorrow) return `Tomorrow, ${startTimePart} (IST)`;

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[nextDate.getMonth()];
    const dateNum = nextDate.getDate();
    return `${month} ${dateNum}, ${startTimePart} (IST)`;
  }

  return `${day}, ${startTimePart} (IST)`;
};
